import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button, Form, Alert } from 'react-bootstrap';
import './UserEdit.scss';

const UserEdit = () => {
  const { userid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    user_name: '',
    user_active: false,
    user_first_name: '',
    user_last_name: ''
  });
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    fetchPermissions();
  }, [userid]);

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get(`/users-api/user/${userid}`);
      setUser(response.data.user);
      setUserPermissions(response.data.permissions);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/users-api/permissions');
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUser((prevUser) => ({
      ...prevUser,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionChange = (permId) => {
    setUserPermissions((prevPermissions) =>
      prevPermissions.includes(permId)
        ? prevPermissions.filter((id) => id !== permId)
        : [...prevPermissions, permId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password && password !== confirmPassword) {
      setError('Password does not match.');
      return;
    }

    setIsSaving(true);
    console.log('Saving edits...');
    try {
      const response = await axios.put(`/users-api/user/${userid}`, {
        ...user,
        password,
        permissions: userPermissions
      });
      if (response.status === 200) {
        setSuccessMessage('User settings successfully saved');
        setTimeout(() => {
          navigate('/users');
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving user details:', error);
      setError('Error saving user details. Please try again later.');
      setIsSaving(false);
    }
  };

  return (
    <div className="user-edit-container">
      <h2>Edit User</h2>
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="accountStatus">
          <Form.Check
            type="checkbox"
            label="Enabled"
            name="user_active"
            checked={user.user_active}
            onChange={handleInputChange}
          />
        </Form.Group>
        <Form.Group controlId="userName">
          <Form.Label>Username</Form.Label>
          <Form.Control
            type="text"
            name="user_name"
            value={user.user_name}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
        <Form.Group controlId="password">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Form.Text className="text-muted">
            Enter a new password here to change it
          </Form.Text>
        </Form.Group>
        <Form.Group controlId="confirmPassword">
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Form.Group>
        <Form.Group controlId="firstName">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            name="user_first_name"
            value={user.user_first_name}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
        <Form.Group controlId="lastName">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            name="user_last_name"
            value={user.user_last_name}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
        <h3>Permissions</h3>
        <div className="permissions-section">
          {permissions.map((permission) => (
            <Form.Check
              key={permission.perm_id}
              type="checkbox"
              id={`permission-${permission.perm_id}`}
              label={permission.perm_name}
              checked={userPermissions.includes(permission.perm_id)}
              onChange={() => handlePermissionChange(permission.perm_id)}
            />
          ))}
        </div>
        <div className="form-actions">
          <Button variant="primary" type="submit" disabled={isSaving} className={isSaving ? 'saving' : ''}>
            Save Edits
          </Button>
          <Button variant="secondary" as={Link} to="/users">
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UserEdit;
